import Image from '../../utils/Image';
import Slider from '../../utils/Slider';
import PriceContainer from '../price/containers/PriceContainer';
import I18n from '../../config/i18n';
import CountryName from '../host/CountryName';
import RatingStars from '../host/RatingStars';
import CapacityIcons from '../host/CapacityIcons';
import ExtraServiceRow from './ExtraServicesRow';
import AccommodationKindName from '../host/AccommodationKindName';
import type { Accommodation } from '../../routes/reducers/types';
import AccommodationLink from '../host/AccommodationLink';

import './HostTeaserBig.css';

interface HostTeaserBigProps {
  id: number;
  rating: number;
  accommodation: Pick<
    Accommodation,
    | 'id'
    | 'slug'
    | 'kind'
    | 'max_persons'
    | 'number_of_rooms'
    | 'number_of_baths'
    | 'extra_service_conditions'
  >;
  image: string;
  images: {
    id: number;
    url: string;
  }[];
  display_title: string;
  locality: string;
  country: string;
  selling_line: string;
  region?: string;
  linkTarget?: '_self' | '_blank';
  showPrices?: boolean;
  suite: boolean;
}

/* eslint-disable camelcase */

export default function HostTeaserBig({
  id,
  rating,
  accommodation,
  image,
  images,
  display_title,
  locality,
  selling_line,
  region,
  country,
  linkTarget,
  showPrices,
  suite,
}: HostTeaserBigProps): JSX.Element {
  function displaySlider(): boolean {
    return !!images;
  }

  return (
    <div className="host-teaser-big">
      <div
        className={`host-teaser-big-header host-teaser-big-header-wrapper-${
          displaySlider() ? 'slider' : 'image'
        }`}
      >
        <AccommodationLink
          className="host-teaser-big-header-image"
          accommodation={accommodation}
          target={linkTarget}
        >
          {displaySlider() ? (
            <div className="host-teaser-big-slider">
              <Slider sliderId={`host-teaser-slider-big-${id}`} images={images} />
            </div>
          ) : (
            <Image className="host-teaser-big-image" src={image} />
          )}
          <div className="host-teaser-big-header-location">{display_title}</div>
        </AccommodationLink>
      </div>
      <div className="host-teaser-big-top-line">
        <div className="host-teaser-big-rating">
          <RatingStars rating={rating} />
        </div>
        <div>
          <CapacityIcons
            maxPersons={accommodation.max_persons}
            numberOfRooms={accommodation.number_of_rooms}
            numberOfBaths={accommodation.number_of_baths}
          />
        </div>
      </div>
      <AccommodationLink accommodation={accommodation} target={linkTarget}>
        <h3 className="host-teaser-big-title">{selling_line}</h3>
      </AccommodationLink>
      <div className="host-teaser-big-location">
        <span>
          <CountryName countryCode={country} />
        </span>
        {region ? <span>{region}</span> : null}
        {locality ? <span>{locality}</span> : null}
      </div>
      <div className="host-teaser-big-info">
        <span>
          <AccommodationKindName accommodation={accommodation} suite={suite} />
        </span>
      </div>
      <div>
        <ExtraServiceRow extraServiceConditions={accommodation.extra_service_conditions} />
      </div>
      {showPrices ? (
        <div className="host-teaser-big-price">
          <PriceContainer accommodation={accommodation} renderMatchingAndAlternatives />
        </div>
      ) : null}
      <div className="host-teaser-big-action">
        <AccommodationLink
          accommodation={accommodation}
          className="host-teaser-big-button"
          target={linkTarget}
        >
          {I18n.t('host_teaser.button')}
        </AccommodationLink>
      </div>
    </div>
  );
}

const defaultProps: Pick<HostTeaserBigProps, 'linkTarget' | 'region' | 'showPrices'> = {
  linkTarget: undefined,
  region: undefined,
  showPrices: true,
};

HostTeaserBig.defaultProps = defaultProps;
